import { taskStatusResponseSchema } from '@consigli/types';
const HEX32_REGEX = /^[0-9a-f]{32}$/i;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidTaskId(id) {
    return HEX32_REGEX.test(id) || UUID_REGEX.test(id);
}
export class TaskFailedError extends Error {
    data;
    constructor(task) {
        super(task.user_friendly_message || task.error || 'Task failed');
        this.name = 'TaskFailedError';
        this.data = {
            error_code: task.error_code,
            message_key: task.message_key,
            user_friendly_message: task.user_friendly_message,
            message: task.error,
        };
    }
}
export async function pollTask(fetchFn, schema, options = {}) {
    const { intervalMs = 2000, backoffMultiplier, maxIntervalMs, timeoutMs = 300_000, signal, } = options;
    const deadline = Date.now() + timeoutMs;
    let currentInterval = intervalMs;
    while (true) {
        if (signal?.aborted) {
            throw new DOMException('Polling cancelled', 'AbortError');
        }
        if (Date.now() >= deadline) {
            throw new Error('Task timed out after ' + timeoutMs + 'ms');
        }
        const response = await fetchFn();
        if (response.error) {
            throw response.error;
        }
        const parsed = taskStatusResponseSchema.safeParse(response.data);
        if (!parsed.success) {
            throw new Error(`Invalid task status response: ${parsed.error.message}`);
        }
        const task = parsed.data;
        if (!isValidTaskId(task.task_id)) {
            throw new Error(`Invalid task_id: "${task.task_id}"`);
        }
        const status = task.status.toLowerCase();
        if (status === 'completed' || status === 'success') {
            return schema.parse(task.result);
        }
        if (status === 'failed' || status === 'failure') {
            throw new TaskFailedError(task);
        }
        // Wait before the next poll, but abort immediately if the signal fires.
        const waitMs = currentInterval;
        if (backoffMultiplier) {
            currentInterval = maxIntervalMs
                ? Math.min(currentInterval * backoffMultiplier, maxIntervalMs)
                : currentInterval * backoffMultiplier;
        }
        await new Promise((resolve, reject) => {
            if (signal?.aborted) {
                reject(new DOMException('Polling cancelled', 'AbortError'));
                return;
            }
            const onAbort = () => {
                reject(new DOMException('Polling cancelled', 'AbortError'));
            };
            // No clearTimeout needed: resolving an already-rejected Promise is a silent no-op.
            setTimeout(() => {
                signal?.removeEventListener('abort', onAbort);
                resolve();
            }, waitMs);
            signal?.addEventListener('abort', onAbort, { once: true });
        });
    }
}
